import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { subscribeAuth, signOut } from "../services/auth";
import "./AppShell.css";

// Top-nav + footer wrapper. Every route renders inside this. The "Get the
// PlayAware app" sticky banner lives here so it's consistent across pages.

export default function AppShell({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [bannerOpen, setBannerOpen] = useState(true);
  const location = useLocation();

  useEffect(() => subscribeAuth(setUser), []);

  // Live-subscribe to the current user's app_users doc so the profile
  // chip in the header reflects displayName / username / avatar edits
  // the moment they're saved on /me/edit.
  useEffect(() => {
    if (!user) { setProfile(null); return; }
    return onSnapshot(
        doc(db, "app_users", user.uid),
        (snap) => setProfile(snap.exists() ? snap.data() : null),
        () => setProfile(null),
    );
  }, [user?.uid]);
  useEffect(() => {
    setBannerOpen(localStorage.getItem("pa_banner_dismissed") !== "1");
  }, []);
  const dismissBanner = () => {
    setBannerOpen(false);
    localStorage.setItem("pa_banner_dismissed", "1");
  };

  return (
    <div className="app-shell">
      {/* Moderation / safety strip — present on every page so
          AdSense reviewers (and users) can see we acknowledge UGC
          risks and provide a reporting flow. */}
      <div className="mod-strip">
        <div className="container mod-strip-row">
          <span className="mod-strip-icon">🛡️</span>
          <span className="mod-strip-text">
            Pop Market is a directory of off-platform sellers. Listings are reviewed for
            spam/abuse by automated checks + manual moderation. <strong>Account
            password sharing is forbidden</strong> — we are not affiliated with any
            game publisher.
          </span>
          <a className="mod-strip-report" href="mailto:playawareofficial@gmail.com?subject=Pop+Market+report">
            Report a listing
          </a>
        </div>
      </div>

      <header className="topbar">
        <div className="container topbar-row">
          <Link to="/" className="brand">
            <span className="brand-mark">★</span>
            <span className="brand-name">
              Pop Market <span className="brand-by">· PlayAware</span>
            </span>
          </Link>
          <nav className="top-nav">
            <NavLink to="/" end><span className="top-nav-ico">🏪</span>Browse</NavLink>
            <NavLink to="/calculator"><span className="top-nav-ico">🧮</span>Calculator</NavLink>
            <NavLink to="/calendar"><span className="top-nav-ico">📅</span>Battle Calendar</NavLink>
            <NavLink to="/about"><span className="top-nav-ico">ℹ️</span>About</NavLink>
          </nav>
          <div className="auth-area">
            {user ? (
              <>
                <ProfileChip user={user} profile={profile} />
                <NavLink
                  to="/me/edit"
                  className="auth-btn ghost"
                  title="Edit profile"
                  style={{ padding: "6px 12px" }}
                >
                  Edit
                </NavLink>
                <button
                  className="auth-btn ghost"
                  onClick={() => signOut()}
                  aria-label="Sign out"
                  title="Sign out"
                  style={{ padding: "6px 12px" }}
                >
                  Sign out
                </button>
              </>
            ) : (
              <>
                <NavLink to="/signin" className="auth-btn ghost">Sign in</NavLink>
                <NavLink to="/list" className="auth-btn primary">
                  List on Pop Market
                </NavLink>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="main">{children}</main>

      <footer className="footer">
        <div className="container">
          <div className="footer-row">
            <div className="footer-col">
              <div className="footer-title">Pop Market</div>
              <Link to="/">Browse sellers</Link>
              <Link to="/calculator">Points calculator</Link>
              <Link to="/calendar">Battle calendar</Link>
              <Link to="/list">List as seller</Link>
              <Link to="/signup">Create account</Link>
              <Link to="/signin">Sign in</Link>
            </div>
            <div className="footer-col">
              <div className="footer-title">Info</div>
              <Link to="/about">About</Link>
              <Link to="/about#disclaimer">Disclaimer</Link>
              <Link to="/about#privacy">Privacy</Link>
              <Link to="/about#terms">Terms</Link>
            </div>
            <div className="footer-col">
              <div className="footer-title">PlayAware</div>
              <a href="https://playaware.com" target="_blank" rel="noopener noreferrer">Main app</a>
              <a href="https://playaware.com/showcase" target="_blank" rel="noopener noreferrer">Showcase</a>
            </div>
          </div>
          <div className="footer-fineprint">
            PlayAware is a directory and reputation platform. We do not process
            transactions or mediate disputes between buyers and sellers. Users
            transact at their own risk and are responsible for compliance with
            their game's terms of service.
          </div>
        </div>
      </footer>

      <ProfileChipStyles />

      {bannerOpen && !location.pathname.startsWith("/auth") &&
        !["/signin", "/signup", "/list"].includes(location.pathname) && (
        <div className="install-banner">
          <div className="container install-row">
            <span>📱 Get the <strong>PlayAware</strong> app — track your popularity, achievements, and tournaments in one place.</span>
            <div className="install-cta">
              <a href="https://playaware.com/download" target="_blank" rel="noopener noreferrer" className="install-btn">Install</a>
              <button className="install-dismiss" onClick={dismissBanner} aria-label="Dismiss">×</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Header profile chip — avatar + display name that clicks through to
// the user's own public profile at /u/:username (or /me/edit as a
// fallback while the profile snapshot is still loading, so the user
// isn't blocked from editing their name/username).
function ProfileChip({ user, profile }) {
  const displayName = profile?.displayName
      || user.displayName
      || (user.email || "").split("@")[0]
      || "You";
  const username = profile?.username || null;
  const photoURL = profile?.photoURL || user.photoURL || null;
  const initial = displayName.trim().charAt(0).toUpperCase() || "?";
  // Prefer the public profile URL; fall back to /me/edit so a stuck
  // profile snapshot never traps the user without a way to their
  // settings.
  const href = username ? `/u/${username}` : "/me/edit";

  return (
    <Link to={href} className="pm-profile-chip" aria-label="Open your profile">
      {photoURL ? (
        <img src={photoURL} alt="" className="pm-profile-avatar" />
      ) : (
        <span className="pm-profile-initial">{initial}</span>
      )}
      <span className="pm-profile-name">{displayName}</span>
    </Link>
  );
}

function ProfileChipStyles() {
  return (
    <style>{`
      .pm-profile-chip {
        display: inline-flex; align-items: center; gap: 8px;
        padding: 4px 12px 4px 4px;
        border-radius: 999px;
        border: 1px solid rgba(255,255,255,0.10);
        background: rgba(255,255,255,0.03);
        color: #eaeaef;
        font-size: 13px;
        font-weight: 700;
        text-decoration: none;
        transition: border-color .15s, background .15s;
      }
      .pm-profile-chip:hover {
        border-color: rgba(255,255,255,0.24);
        background: rgba(255,255,255,0.06);
      }
      .pm-profile-avatar {
        width: 26px; height: 26px; border-radius: 50%;
        object-fit: cover; flex-shrink: 0;
      }
      .pm-profile-initial {
        width: 26px; height: 26px; border-radius: 50%;
        background: linear-gradient(135deg, #4c6ef5, #a561ff);
        color: #fff;
        display: inline-flex; align-items: center; justify-content: center;
        font-weight: 900; font-size: 13px;
        flex-shrink: 0;
      }
      .pm-profile-name {
        max-width: 140px;
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      @media (max-width: 640px) {
        .pm-profile-name { max-width: 90px; }
      }
    `}</style>
  );
}
